import React, { useMemo, useState } from 'react';
import Spinner from '../../components/ui/Spinner';
import { MONTH_NAMES, DOW, buildCalendarGrid, ymd, timeToMinutes } from '../../utils/dateUtils';
import { statusColor, statusColorSoft, statusLabel } from '../../utils/statusUtils';

const DAY_ROW_HEIGHT = 64;
const DAY_VIEWPORT_HEIGHT = 420;
const DAY_OVERSCAN = 6;

function keyToDate(key) {
  if (!key) return new Date();
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function dateToKey(date) {
  return ymd(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // Sunday=0
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return addDays(d, mondayOffset);
}

function fmtLongDate(key) {
  const date = keyToDate(key);
  return date.toLocaleDateString('es-MX', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export default function CalendarPanel({ assemblies, loading, onDayClick, onEventClick }) {
  const today = new Date();
  const [calYear,  setCalYear]  = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [viewMode, setViewMode] = useState('month');
  const [selectedDateKey, setSelectedDateKey] = useState(dateToKey(today));
  const [dayScrollTop, setDayScrollTop] = useState(0);

  const cells = buildCalendarGrid(calYear, calMonth);
  const selectedDate = keyToDate(selectedDateKey);

  const byDate = useMemo(() => {
    const grouped = new Map();
    assemblies.forEach((assembly) => {
      const key = String(assembly.scheduledDate || '').slice(0, 10);
      if (!key) return;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(assembly);
    });

    grouped.forEach((items, key) => {
      grouped.set(
        key,
        items.sort((a, b) => {
          const byStart = timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
          if (byStart !== 0) return byStart;

          const byEnd = timeToMinutes(a.endTime) - timeToMinutes(b.endTime);
          if (byEnd !== 0) return byEnd;

          return String(a.address || '').localeCompare(String(b.address || ''));
        })
      );
    });

    return grouped;
  }, [assemblies]);

  function assembliesOnDay(day) {
    const key = ymd(calYear, calMonth, day);
    return byDate.get(key) || [];
  }

  function assembliesForKey(key) {
    return byDate.get(key) || [];
  }

  function prevMonth() {
    setCalMonth(m => { if (m === 0) { setCalYear(y => y - 1); return 11; } return m - 1; });
  }

  function nextMonth() {
    setCalMonth(m => { if (m === 11) { setCalYear(y => y + 1); return 0; } return m + 1; });
  }

  function durationLabel(a) {
    const diff = timeToMinutes(a.endTime) - timeToMinutes(a.startTime);
    return diff <= 0 ? a.startTime : `${a.startTime} – ${a.endTime}`;
  }

  function syncAnchorDate(nextDate) {
    setCalYear(nextDate.getFullYear());
    setCalMonth(nextDate.getMonth());
    setSelectedDateKey(dateToKey(nextDate));
    setDayScrollTop(0);
  }

  function goPrevRange() {
    if (viewMode === 'month') {
      prevMonth();
      return;
    }

    const delta = viewMode === 'week' ? -7 : -1;
    syncAnchorDate(addDays(selectedDate, delta));
  }

  function goNextRange() {
    if (viewMode === 'month') {
      nextMonth();
      return;
    }

    const delta = viewMode === 'week' ? 7 : 1;
    syncAnchorDate(addDays(selectedDate, delta));
  }

  function goToday() {
    syncAnchorDate(new Date());
  }

  function onChangeAnchorDate(value) {
    if (!value) return;
    syncAnchorDate(keyToDate(value));
  }

  function toolbarTitle() {
    if (viewMode === 'month') return `${MONTH_NAMES[calMonth]} ${calYear}`;
    if (viewMode === 'day') return fmtLongDate(selectedDateKey);

    const weekStart = startOfWeek(selectedDate);
    const weekEnd = addDays(weekStart, 6);
    return `${weekStart.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })} - ${weekEnd.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  }

  function renderEventButton(a, className = 'cal-event') {
    return (
      <button
        key={a.id}
        className={className}
        style={{
          borderLeftColor: statusColor(a.status),
          backgroundColor: statusColorSoft(a.status),
        }}
        onClick={ev => { ev.stopPropagation(); onEventClick(a); }}
        title={`${a.address} · ${statusLabel(a.status)}`}
      >
        <span className="cal-event-time">{durationLabel(a)}</span>
        <span className="cal-event-place">{a.address}</span>
      </button>
    );
  }

  function renderMonthView() {
    return (
      <div className="cal-grid">
        {DOW.map(d => <div key={d} className="cal-dow">{d}</div>)}

        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${i}`} className="cal-cell cal-cell--empty" />;
          const dayAssemblies = assembliesOnDay(day);
          const key = ymd(calYear, calMonth, day);
          const isToday =
            today.getFullYear() === calYear &&
            today.getMonth() === calMonth &&
            today.getDate() === day;

          return (
            <div
              key={key}
              className={`cal-cell${isToday ? ' cal-cell--today' : ''}`}
              onClick={() => onDayClick(key)}
            >
              <span className="cal-day-num">{day}</span>
              <div className="cal-events">
                {dayAssemblies.map(a => renderEventButton(a))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderWeekView() {
    const weekStart = startOfWeek(selectedDate);
    const days = Array.from({ length: 7 }).map((_, idx) => addDays(weekStart, idx));

    return (
      <div className="cal-week-grid">
        {days.map((date) => {
          const key = dateToKey(date);
          const events = assembliesForKey(key);
          const isToday = dateToKey(today) === key;
          return (
            <section key={key} className={`cal-week-day${isToday ? ' cal-week-day--today' : ''}`}>
              <button
                className="cal-week-head"
                onClick={() => {
                  setViewMode('day');
                  setSelectedDateKey(key);
                  setDayScrollTop(0);
                }}
              >
                <strong>{DOW[(date.getDay() + 6) % 7]}</strong>
                <span>{date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit' })}</span>
              </button>
              <div className="cal-week-events">
                {events.length === 0 && <p className="cal-empty">Sin asambleas</p>}
                {events.map(a => renderEventButton(a, 'cal-event cal-event--compact'))}
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  function renderDayView() {
    const dayEvents = assembliesForKey(selectedDateKey);
    const total = dayEvents.length;
    const visible = Math.ceil(DAY_VIEWPORT_HEIGHT / DAY_ROW_HEIGHT);
    const startIndex = Math.max(0, Math.floor(dayScrollTop / DAY_ROW_HEIGHT) - DAY_OVERSCAN);
    const endIndex = Math.min(total, startIndex + visible + (DAY_OVERSCAN * 2));
    const topPad = startIndex * DAY_ROW_HEIGHT;
    const bottomPad = (total - endIndex) * DAY_ROW_HEIGHT;
    const visibleEvents = dayEvents.slice(startIndex, endIndex);

    return (
      <section className="cal-day-view">
        <div className="cal-day-header">
          <h3>{fmtLongDate(selectedDateKey)}</h3>
          <span>{total} asamblea(s)</span>
        </div>

        <div
          className="cal-day-scroll"
          onScroll={event => setDayScrollTop(event.currentTarget.scrollTop)}
        >
          {total === 0 && <p className="cal-empty">No hay asambleas para este día.</p>}
          {total > 0 && (
            <div style={{ paddingTop: topPad, paddingBottom: bottomPad }}>
              {visibleEvents.map(a => (
                <div key={a.id} className="cal-day-row" style={{ height: DAY_ROW_HEIGHT }}>
                  {renderEventButton(a, 'cal-event cal-event--day')}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <main className="calendar-panel">
      <div className="cal-toolbar">
        <div className="cal-nav">
          <button className="btn-icon" onClick={goPrevRange}>&#8249;</button>
          <h2 className="cal-month-label">{toolbarTitle()}</h2>
          <button className="btn-icon" onClick={goNextRange}>&#8250;</button>
          <button className="btn-ghost" onClick={goToday}>Hoy</button>
        </div>
        <div className="cal-actions">
          <div className="cal-view-switch" role="tablist" aria-label="Cambiar vista de calendario">
            <button
              className={viewMode === 'month' ? 'active' : ''}
              onClick={() => setViewMode('month')}
            >
              Mes
            </button>
            <button
              className={viewMode === 'week' ? 'active' : ''}
              onClick={() => setViewMode('week')}
            >
              Semana
            </button>
            <button
              className={viewMode === 'day' ? 'active' : ''}
              onClick={() => setViewMode('day')}
            >
              Día
            </button>
          </div>
          <input
            type="date"
            className="cal-date-input"
            value={selectedDateKey}
            onChange={e => onChangeAnchorDate(e.target.value)}
          />
          <button className="btn-primary" onClick={() => onDayClick(selectedDateKey)}>
            + Nueva Asamblea
          </button>
        </div>
      </div>

      {loading && <div className="cal-loading"><Spinner /> Cargando asambleas…</div>}

      {viewMode === 'month' && renderMonthView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'day' && renderDayView()}

      <div className="cal-legend">
        {[['pending','Pendiente'],['in_progress','En curso'],['completed','Completada'],['canceled','Cancelada']].map(([s, l]) => (
          <span key={s} className="legend-item">
            <span className="legend-dot" style={{ background: statusColor(s) }} />
            {l}
          </span>
        ))}
      </div>
    </main>
  );
}
